import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import MarketingShell from "./MarketingShell";
import { Upload, FileCsv, CheckCircle, Warning, ArrowRight } from "@phosphor-icons/react";

/* CSV Import Wizard — 4 steps:
 *   1. Upload        (file + metadata: vendor, provenance, consent, acquisition date)
 *   2. Map columns   (confirm/override auto-detected mapping)
 *   3. Validate      (dry-run counts before commit)
 *   4. Import        (commit with save-as-list checkbox)
 *
 * Phase 1 scope: no chunked upload (single POST), no preview-first-10-rows widget,
 * no field-type inference beyond string. Keep it robust and readable.
 */
export default function CSVImportWizardPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);

  // Upload metadata
  const [name, setName] = useState("");
  const [vendor, setVendor] = useState("");
  const [provenance, setProvenance] = useState("");
  const [consent, setConsent] = useState("opt_in");
  const [acquiredOn, setAcquiredOn] = useState("");

  // Batch state
  const [batch, setBatch] = useState(null);  // server response from /upload
  const [columnMap, setColumnMap] = useState({});
  const [validation, setValidation] = useState(null);

  // Commit state
  const [saveAsList, setSaveAsList] = useState(true);
  const [listName, setListName] = useState("");
  const [finalConsent, setFinalConsent] = useState("opt_in");
  const [commitResult, setCommitResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Drag-and-drop UX state for the Step-1 dropzone.
  const [dragActive, setDragActive] = useState(false);

  // Accept the first CSV-shaped file from the OS, ignore the rest.
  const acceptDroppedFile = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const candidate = Array.from(fileList).find((f) => {
      const n = (f.name || "").toLowerCase();
      return n.endsWith(".csv") || f.type === "text/csv" || f.type === "application/vnd.ms-excel";
    });
    if (!candidate) {
      toast.error("That file isn't a CSV — drop a .csv file or click to pick one.");
      return;
    }
    setFile(candidate);
    if (!name.trim()) setName(candidate.name.replace(/\.csv$/i, ""));
  };

  /* ── Step 1: Upload ─────────────────────────────────────────────────── */
  const doUpload = async () => {
    if (!file) return toast.error("Pick a CSV file first");
    if (!name.trim()) return toast.error("Give this import a name");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name);
      if (vendor) fd.append("vendor", vendor);
      if (provenance) fd.append("provenance_note", provenance);
      fd.append("consent_declaration", consent);
      if (acquiredOn) fd.append("acquisition_date", acquiredOn);
      const r = await api.post("/marketing/imports/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBatch(r.data);
      setColumnMap(r.data.auto_column_map || {});
      setListName(name);
      setFinalConsent(consent);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
  };

  /* ── Step 2: Map columns ─────────────────────────────────────────────── */
  const TARGET_OPTIONS = [
    { v: "", label: "— Ignore —" },
    { v: "email", label: "Email (required)" },
    { v: "first_name", label: "First name" },
    { v: "last_name", label: "Last name" },
    { v: "full_name", label: "Full name" },
    { v: "company_name", label: "Company" },
    { v: "job_title", label: "Job title" },
    { v: "phone", label: "Work phone" },
    { v: "cell_phone", label: "Cell phone" },
    { v: "country", label: "Country" },
  ];

  const doValidate = async () => {
    if (!Object.values(columnMap).includes("email")) {
      return toast.error("You must map one column to 'Email'");
    }
    setBusy(true);
    try {
      const r = await api.post(`/marketing/imports/${batch.id}/validate`, {
        column_map: columnMap,
      });
      setValidation(r.data);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation failed");
    }
    setBusy(false);
  };

  /* ── Step 4: Commit ──────────────────────────────────────────────────── */
  const doCommit = async () => {
    setBusy(true);
    try {
      const payload = {
        final_consent_declaration: finalConsent,
        save_as_list_name: saveAsList ? listName : null,
      };
      const r = await api.post(`/marketing/imports/${batch.id}/commit`, payload);
      setCommitResult(r.data);
      setStep(4);
      toast.success(`Imported ${r.data.imported} · Updated ${r.data.updated}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
    setBusy(false);
  };

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <MarketingShell>
      {/* Step rail */}
      <div className="flex gap-0.5 mb-4 bg-white border border-[#E5E5E5]">
        {[
          { n: 1, label: "Upload" },
          { n: 2, label: "Map Columns" },
          { n: 3, label: "Validate" },
          { n: 4, label: "Done" },
        ].map(s => (
          <div key={s.n}
               className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-wider ${
                 s.n === step ? "bg-[#0055FF] text-white"
                 : s.n < step ? "bg-[#F0FDF4] text-[#16A34A]"
                 : "bg-white text-[#A3A3A3]"
               }`}
               data-testid={`import-step-${s.n}`}>
            {s.n}. {s.label}
          </div>
        ))}
      </div>

      {/* Step 1 — Upload */}
      {step === 1 && (
        <div className="bg-white border border-[#E5E5E5] p-6 space-y-5">
          <h2 className="text-lg font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Upload CSV</h2>

          <Field label="Import name" required>
            <Input val={name} onChange={setName} placeholder="Gartner Conference 2026 attendees" testid="import-name" />
          </Field>

          <label className="block cursor-pointer" data-testid="file-drop"
                 onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                 onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!dragActive) setDragActive(true); }}
                 onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                 onDrop={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setDragActive(false);
                   acceptDroppedFile(e.dataTransfer?.files);
                 }}>
            <div className={`border-2 border-dashed p-8 text-center transition-colors ${
              dragActive ? "border-[#0055FF] bg-[#EFF6FF]" :
              file       ? "border-[#16A34A] bg-[#F0FDF4]" :
                           "border-[#E5E5E5] hover:border-[#0055FF]"
            }`}>
              <Upload size={24} className={`mx-auto ${dragActive ? "text-[#0055FF]" : "text-[#A3A3A3]"}`} />
              <p className="text-sm font-semibold text-[#0A0A0A] mt-2">
                {file ? file.name : (dragActive ? "Drop the CSV to upload it" : "Click or drag a CSV here")}
              </p>
              <p className="text-xs text-[#A3A3A3] mt-1">
                Max 20 MB · UTF-8 encoding · first row = headers
              </p>
            </div>
            <input type="file" accept=".csv,text/csv" className="hidden"
                   onChange={e => acceptDroppedFile(e.target.files)} data-testid="file-input" />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Vendor / source" hint="Who did these contacts come from?">
              <Input val={vendor} onChange={setVendor} placeholder="LeadGenPro, Eventbrite, tradeshow…" testid="vendor" />
            </Field>
            <Field label="Acquisition date" hint="When did you get this list?">
              <Input type="date" val={acquiredOn} onChange={setAcquiredOn} testid="acquired-on" />
            </Field>
            <div className="md:col-span-2">
              <Field label="Provenance note" hint="How was this list captured? (Used for audit trail)">
                <textarea value={provenance} onChange={e => setProvenance(e.target.value)} rows={2}
                          className="w-full px-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                          data-testid="provenance" />
              </Field>
            </div>
          </div>

          <Field label="Consent declaration" required hint="What is the consent status of these contacts?">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { v: "opt_in", label: "Opted in", desc: "Explicit marketing consent captured" },
                { v: "implicit", label: "Legitimate interest", desc: "Existing business relationship" },
                { v: "unknown", label: "Unknown", desc: "Consent status unclear — review required" },
              ].map(o => (
                <label key={o.v}
                       className={`block p-3 cursor-pointer border ${consent === o.v ? "border-[#0055FF] bg-[#EFF6FF]" : "border-[#E5E5E5] hover:border-[#0055FF]"}`}
                       data-testid={`consent-${o.v}`}>
                  <input type="radio" checked={consent === o.v} onChange={() => setConsent(o.v)} className="mr-2" />
                  <span className="text-sm font-bold text-[#0A0A0A]">{o.label}</span>
                  <p className="text-xs text-[#525252] mt-1">{o.desc}</p>
                </label>
              ))}
            </div>
          </Field>

          <div className="flex justify-end gap-2 pt-4 border-t border-[#F5F5F5]">
            <button onClick={() => nav("/marketing/imports")} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider">Cancel</button>
            <button onClick={doUpload} disabled={busy || !file || !name.trim()}
                    className="h-9 px-5 bg-[#0055FF] hover:bg-[#0040CC] disabled:opacity-40 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                    data-testid="upload-next">
              {busy ? "Uploading…" : "Next: Map Columns"}<ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2 — Map columns */}
      {step === 2 && batch && (
        <div className="bg-white border border-[#E5E5E5] p-6">
          <h2 className="text-lg font-black tracking-tight mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Map Columns</h2>
          <p className="text-xs text-[#525252] mb-4">
            <FileCsv size={12} className="inline mr-1" />
            <strong>{batch.row_count}</strong> data rows detected. Review the auto-mapped fields below.
          </p>

          <table className="w-full text-sm" data-testid="column-map-table">
            <thead className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr className="border-b border-[#E5E5E5]">
                <th className="text-left font-bold py-2">CSV Column</th>
                <th className="text-left font-bold py-2 pl-4">Maps to</th>
              </tr>
            </thead>
            <tbody>
              {batch.headers.map(h => (
                <tr key={h} className="border-b border-[#F5F5F5]">
                  <td className="py-2 font-mono text-xs text-[#0A0A0A]">{h}</td>
                  <td className="py-2 pl-4">
                    <select
                      value={columnMap[h] || ""}
                      onChange={e => setColumnMap({ ...columnMap, [h]: e.target.value || undefined })}
                      className="h-8 px-2 text-sm border border-[#E5E5E5] min-w-[200px]"
                      data-testid={`map-${h}`}>
                      {TARGET_OPTIONS.map(o =>
                        <option key={o.v} value={o.v}>{o.label}</option>
                      )}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="text-[11px] text-[#A3A3A3] mt-3">
            Any column not mapped here will still be stored on each contact as a <code>custom_field</code>.
          </p>

          <div className="flex justify-between pt-4 border-t border-[#F5F5F5] mt-4">
            <button onClick={() => setStep(1)} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider">Back</button>
            <button onClick={doValidate} disabled={busy}
                    className="h-9 px-5 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                    data-testid="validate-next">
              {busy ? "Validating…" : "Next: Validate"}<ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3 — Validate */}
      {step === 3 && validation && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <ValStat label="Ready to import" value={validation.ready} good data-testid="val-ready" />
            <ValStat label="Already exists" value={validation.already_exists_will_update} note="Will be updated" data-testid="val-exists" />
            <ValStat label="Suppressed" value={validation.already_suppressed} note="Will be flagged" warn data-testid="val-suppressed" />
            <ValStat label="Duplicates" value={validation.duplicate_in_file} note="Skipped" warn data-testid="val-dup" />
            <ValStat label="Invalid emails" value={validation.invalid_email} note="Skipped" bad data-testid="val-invalid" />
          </div>

          <div className="bg-white border border-[#E5E5E5] p-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-[#525252] mb-3">Final options</h3>
            <label className="flex items-start gap-2 mb-3 cursor-pointer">
              <input type="checkbox" checked={saveAsList} onChange={e => setSaveAsList(e.target.checked)} className="mt-0.5" data-testid="save-as-list" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#0A0A0A]">Save as a reusable list</p>
                <p className="text-xs text-[#A3A3A3]">Campaigns can target this list directly.</p>
                {saveAsList && (
                  <input value={listName} onChange={e => setListName(e.target.value)}
                         placeholder="List name"
                         className="mt-2 w-full h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]"
                         data-testid="list-name" />
                )}
              </div>
            </label>

            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] block mt-4">Confirm consent declaration</label>
            <select value={finalConsent} onChange={e => setFinalConsent(e.target.value)}
                    className="mt-1 h-9 px-3 text-sm border border-[#E5E5E5]" data-testid="final-consent">
              <option value="opt_in">Opted in — explicit marketing consent</option>
              <option value="implicit">Legitimate interest — existing business relationship</option>
              <option value="unknown">Unknown — review required</option>
            </select>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider">Back</button>
            <button onClick={doCommit} disabled={busy || !validation.ready}
                    className="h-10 px-6 bg-[#0055FF] hover:bg-[#0040CC] disabled:opacity-40 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                    data-testid="commit-btn">
              {busy ? "Importing…" : `Import ${validation.ready} contacts`}<ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Step 4 — Done */}
      {step === 4 && commitResult && (
        <div className="bg-white border border-[#E5E5E5] p-8 text-center">
          <CheckCircle size={42} weight="fill" className="mx-auto text-[#16A34A]" />
          <h2 className="text-2xl font-black tracking-tight mt-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="import-done">
            Import complete
          </h2>
          <div className="inline-grid grid-cols-3 gap-6 mt-4 text-center">
            <div><p className="text-3xl font-black text-[#0A0A0A] tabular-nums">{commitResult.imported}</p><p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Imported</p></div>
            <div><p className="text-3xl font-black text-[#0A0A0A] tabular-nums">{commitResult.updated}</p><p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Updated</p></div>
            <div><p className="text-3xl font-black text-[#DC2626] tabular-nums">{commitResult.skipped}</p><p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Skipped</p></div>
          </div>
          <div className="flex gap-2 justify-center mt-6">
            <button onClick={() => nav("/marketing/imports")} className="h-9 px-4 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider">View all imports</button>
            {commitResult.list_id && (
              <button onClick={() => nav("/marketing/lists")} className="h-9 px-4 border border-[#0055FF] text-[#0055FF] text-xs font-bold uppercase tracking-wider">View saved list</button>
            )}
            <button onClick={() => nav("/marketing/campaigns/new")} className="h-9 px-4 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider">Create campaign</button>
          </div>
        </div>
      )}
    </MarketingShell>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3] block mb-1">
        {label} {required && <span className="text-[#DC2626]">*</span>}
      </label>
      {children}
      {hint && <p className="text-[11px] text-[#A3A3A3] mt-1">{hint}</p>}
    </div>
  );
}

function Input({ val, onChange, placeholder, type = "text", testid }) {
  return (
    <input type={type} value={val} onChange={e => onChange(e.target.value)}
           placeholder={placeholder} data-testid={testid}
           className="w-full h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]" />
  );
}

function ValStat({ label, value, note, good, warn, bad, ...rest }) {
  const color = good ? "#16A34A" : bad ? "#DC2626" : warn ? "#D97706" : "#0A0A0A";
  return (
    <div className="bg-white border border-[#E5E5E5] p-3" {...rest}>
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">{label}</p>
      <p className="text-2xl font-black tabular-nums" style={{ color, fontFamily: "Cabinet Grotesk, sans-serif" }}>{value}</p>
      {note && <p className="text-[11px] text-[#525252]">{note}</p>}
    </div>
  );
}
